/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const roles = currentUser?.roles || [];
  const permissions = currentUser?.permissions || [];
  const has = (code: string) =>
    roles.includes('super_admin') || permissions.includes(code);
  const isSuperAdmin =
    currentUser?.access === 'admin' || roles.includes('super_admin');
  const isCompanyAdmin =
    isSuperAdmin ||
    roles.includes('company_admin') ||
    has('customer.admin') ||
    has('user.write');

  return {
    /** 超级管理员 / 开发者 */
    canAdmin: Boolean(isSuperAdmin),
    canSuperAdmin: Boolean(isSuperAdmin),
    /** 公司管理者（含超管） */
    canCompanyAdmin: Boolean(isCompanyAdmin),
    /** 业务「管理员」菜单：全公司客户/设备/项目 */
    canSalesAdmin: Boolean(isSuperAdmin || has('customer.admin')),
    /** 可新建/编辑客户（我的客户） */
    canCreateCustomer: Boolean(
      isSuperAdmin || has('customer.write') || has('customer.admin'),
    ),
    /** 员工/部门/职位等公司组织 */
    canManageCompany: Boolean(
      isSuperAdmin || has('user.write') || has('org.write'),
    ),
    /** 权限组配置（开发者） */
    canManageRoles: Boolean(isSuperAdmin || has('role.write')),
    /** 我的工作小组 / 我的任务 */
    canWorkGroups: Boolean(
      isSuperAdmin || has('workgroup.write') || has('workgroup.admin'),
    ),
    /** 出差 */
    canTrips: Boolean(
      isSuperAdmin ||
        has('trip.read') ||
        has('trip.write') ||
        has('workgroup.write') ||
        has('customer.write'),
    ),
    canTripAdmin: Boolean(
      isSuperAdmin || has('trip.admin') || has('trip.approve') || has('customer.admin'),
    ),
    /** 账号中心：全公司工作小组管理 */
    canWorkGroupAdmin: Boolean(isSuperAdmin || has('workgroup.admin')),
    /** 登录/企微/天眼查/OSS 等开发者设置 */
    canDeveloperSettings: Boolean(isSuperAdmin || has('settings.write')),
    /** 字段字典 / 设备目录 / 客户分配等业务配置 */
    canBusinessSettings: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('dict.write') ||
        has('customer.admin'),
    ),
    /** 系统设置一级菜单 */
    canAccessSettings: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('settings.write') ||
        has('dict.read') ||
        has('dict.write') ||
        has('customer.admin'),
    ),
    /** 账号中心一级菜单 */
    canAccessSystem: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('user.read') ||
        has('org.write') ||
        has('workgroup.admin') ||
        has('role.write'),
    ),
    /** 产品中心 */
    canProducts: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('product.read') ||
        has('product.write') ||
        has('inventory.read') ||
        has('inventory.write') ||
        has('inventory.admin'),
    ),
    /** 库存管理分组 */
    canInventory: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('inventory.read') ||
        has('inventory.write') ||
        has('inventory.admin'),
    ),
    canInventoryWrite: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('inventory.write') ||
        has('inventory.admin'),
    ),
    canInventoryAdmin: Boolean(
      isSuperAdmin || isCompanyAdmin || has('inventory.admin'),
    ),
    /** 订单：报价单 / 合约 */
    canOrders: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('quote.read') ||
        has('quote.write') ||
        has('contract.read') ||
        has('contract.write'),
    ),
    canQuoteWrite: Boolean(
      isSuperAdmin || isCompanyAdmin || has('quote.write'),
    ),
    canContractWrite: Boolean(
      isSuperAdmin || isCompanyAdmin || has('contract.write'),
    ),
    canSalesDocApprove: Boolean(
      isSuperAdmin ||
        isCompanyAdmin ||
        has('sales_doc.approve') ||
        has('customer.admin'),
    ),
    canDocTemplateAdmin: Boolean(
      isSuperAdmin || isCompanyAdmin || has('doc_template.admin'),
    ),
  };
}
